import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
} from "@mui/material";
import { BugReport, Lightbulb, Search, Circle } from "@mui/icons-material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const FeedbackList = ({
  onSelectFeedback,
  selectedFeedbackId,
  feedbackType,
}) => {
  const { feedback = [], users = [] } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter by type and search term
  const filteredFeedback = useMemo(() => {
    let filtered = feedback.filter(
      (item) => item.feedbackType === feedbackType,
    );

    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const user = users.find((u) => u.userId === item.userId);
        const displayName =
          user?.displayName || user?.name || user?.email || "";
        return (
          item.userId?.toLowerCase().includes(lowerSearch) ||
          displayName.toLowerCase().includes(lowerSearch) ||
          item.whatHappened?.toLowerCase().includes(lowerSearch) ||
          item.featureDescription?.toLowerCase().includes(lowerSearch) ||
          item.feedback?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    return filtered;
  }, [feedback, feedbackType, searchTerm, users]);

  // Count unread
  const unreadCount = useMemo(() => {
    return filteredFeedback.filter((item) => !item.read).length;
  }, [filteredFeedback]);

  // Paginate
  const paginatedFeedback = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredFeedback.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredFeedback, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredFeedback.length / rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
    setPage(1);
  };

  const getUserDisplayName = (userId) => {
    const user = users.find((u) => u.userId === userId);
    return (
      user?.displayName ||
      user?.name ||
      user?.email?.split("@")[0] ||
      userId?.substring(0, 8) + "..."
    );
  };

  const getPreviewText = (item) => {
    if (feedbackType === "bug-report") {
      return (
        item.whatHappened?.substring(0, 60) +
        (item.whatHappened?.length > 60 ? "..." : "")
      );
    } else if (feedbackType === "feature-request") {
      return (
        item.featureDescription?.substring(0, 60) +
        (item.featureDescription?.length > 60 ? "..." : "")
      );
    }
    return "";
  };

  const Icon = feedbackType === "bug-report" ? BugReport : Lightbulb;
  const iconColor = feedbackType === "bug-report" ? "#d32f2f" : "#813773";

  if (filteredFeedback.length === 0 && !searchTerm) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No{" "}
          {feedbackType === "bug-report" ? "bug reports" : "feature requests"}{" "}
          yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Search and Controls */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
          }}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: "0 !important" }}
            >
              {filteredFeedback.length} item
              {filteredFeedback.length !== 1 ? "s" : ""}
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} new`}
                size="small"
                color="error"
                sx={{ height: 20 }}
              />
            )}
          </Box>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              variant="outlined"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              label="Per page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Feedback List */}
      {filteredFeedback.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No results match your search.
          </Typography>
        </Box>
      ) : (
        <>
          <List
            sx={{ p: 0, flex: 1, overflow: "auto", minHeight: 0 }}
            className="scroller"
          >
            {paginatedFeedback.map((item) => (
              <ListItem
                key={item.feedbackId}
                disablePadding
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
              >
                <ListItemButton
                  onClick={() => onSelectFeedback(item.feedbackId)}
                  selected={selectedFeedbackId === item.feedbackId}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&.Mui-selected": { bgcolor: "#e8f5e9" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Avatar sx={{ bgcolor: iconColor, width: 36, height: 36 }}>
                      <Icon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={!item.read ? "bold" : "normal"}
                          sx={{ mb: "0 !important" }}
                          noWrap
                        >
                          {getUserDisplayName(item.userId)}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {!item.read && (
                            <Circle sx={{ fontSize: 8, color: "#197b68" }} />
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: "0 !important" }}
                          >
                            {dayjs(item.createdAt).format("DD/MM/YY")}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: "0 !important" }}
                        noWrap
                      >
                        {getPreviewText(item)}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FeedbackList;
